import { useCallback } from 'react';

import { createActionsHook, createHook, createStore } from 'react-sweet-state';

import {
	SUGGESTED_PROMPT_TILE_STREAM_MESSAGE_TYPE,
	type SuggestedPromptTile,
	type SuggestedPromptTileStreamMessage,
} from '@atlassian/conversation-assistant-service-api/types/assistance-service';
import {
	type AssistanceServiceInjectedParams,
	type AssistanceServiceInputParam,
	createHookWithAssistanceService,
} from '@atlassian/conversation-assistant-service/services/assistance-service/main';

export const PRIMARY_PROMPTS_COUNT = 3;
export const SECONDARY_PROMPTS_COUNT = 6;
export const PROMPT_SUGGESTIONS_OFFSET = 0;

const TOTAL_PROMPT_SUGGESTIONS_COUNT = PRIMARY_PROMPTS_COUNT + SECONDARY_PROMPTS_COUNT;

const getPrimaryPrompts = (tiles: SuggestedPromptTile[]) => tiles.slice(0, PRIMARY_PROMPTS_COUNT);
const getSecondaryPrompts = (tiles: SuggestedPromptTile[]) => tiles.slice(PRIMARY_PROMPTS_COUNT);

const shuffleArray = <T,>(items: T[]): T[] => {
	const shuffled = [...items];

	for (let i = shuffled.length - 1; i > 0; i--) {
		const randomIndex = Math.floor(Math.random() * (i + 1));
		[shuffled[i], shuffled[randomIndex]] = [shuffled[randomIndex], shuffled[i]];
	}

	return shuffled;
};

const shufflePromptBuckets = ({
	primaryPrompts,
	secondaryPrompts,
}: {
	primaryPrompts: SuggestedPromptTile[];
	secondaryPrompts: SuggestedPromptTile[];
}) => {
	const shuffledPrompts = shuffleArray([...primaryPrompts, ...secondaryPrompts]);

	return {
		primaryPrompts: getPrimaryPrompts(shuffledPrompts),
		secondaryPrompts: getSecondaryPrompts(shuffledPrompts),
	};
};

const parsePromptSuggestionsStream = async ({
	generator,
	onPrimaryPromptsReady,
	onSecondaryPromptsReady,
}: {
	generator: AsyncGenerator<SuggestedPromptTileStreamMessage>;
	onPrimaryPromptsReady: (tiles: SuggestedPromptTile[]) => void;
	onSecondaryPromptsReady: (tiles: SuggestedPromptTile[]) => void;
}): Promise<SuggestedPromptTile[]> => {
	const tiles: SuggestedPromptTile[] = [];

	try {
		for await (const chunk of generator) {
			if (chunk.type !== SUGGESTED_PROMPT_TILE_STREAM_MESSAGE_TYPE) {
				continue;
			}

			tiles.push(chunk.message);

			if (tiles.length === PRIMARY_PROMPTS_COUNT) {
				onPrimaryPromptsReady(getPrimaryPrompts(tiles));
			}

			if (tiles.length === TOTAL_PROMPT_SUGGESTIONS_COUNT) {
				onSecondaryPromptsReady(getSecondaryPrompts(tiles));
				break;
			}
		}
	} catch (error) {
		if (tiles.length === 0) {
			throw error;
		}
	} finally {
		// If stream returns < TOTAL_PROMPT_SUGGESTIONS_COUNT (on either success or partial failure), return whatever we have
		if (tiles.length < PRIMARY_PROMPTS_COUNT) {
			onPrimaryPromptsReady(getPrimaryPrompts(tiles));
		}
		if (tiles.length < TOTAL_PROMPT_SUGGESTIONS_COUNT) {
			onSecondaryPromptsReady(getSecondaryPrompts(tiles));
		}
	}

	return tiles.slice(0, TOTAL_PROMPT_SUGGESTIONS_COUNT);
};

export type UsePromptSuggestionsState = {
	data: SuggestedPromptTile[] | null;
	primaryPrompts: SuggestedPromptTile[] | null;
	secondaryPrompts: SuggestedPromptTile[] | null;
	isLoading: boolean;
	isPrimaryPromptsLoading: boolean;
	isSecondaryPromptsLoading: boolean;
	error: Error | null;
};

export type UsePromptSuggestionsReturn = UsePromptSuggestionsState & {
	loadSuggestions: () => Promise<void>;
	shuffleSuggestions: () => void;
};

const getInitialState = (): UsePromptSuggestionsState => ({
	data: null,
	primaryPrompts: null,
	secondaryPrompts: null,
	isLoading: false,
	isPrimaryPromptsLoading: false,
	isSecondaryPromptsLoading: false,
	error: null,
});

export const store = createStore({
	name: 'promptSuggestions',
	initialState: getInitialState(),
	actions: {
		reset:
			() =>
			({ setState }) => {
				setState(getInitialState());
			},
		getState:
			() =>
			({ getState }) =>
				getState(),
		setLoading:
			() =>
			({ setState }) => {
				setState({
					data: null,
					primaryPrompts: null,
					secondaryPrompts: null,
					isLoading: true,
					isPrimaryPromptsLoading: true,
					isSecondaryPromptsLoading: true,
					error: null,
				});
			},
		setPrimaryPrompts:
			(primaryPrompts: SuggestedPromptTile[]) =>
			({ setState }) => {
				setState({ primaryPrompts, isPrimaryPromptsLoading: false });
			},
		setSecondaryPrompts:
			(secondaryPrompts: SuggestedPromptTile[]) =>
			({ setState }) => {
				setState({ secondaryPrompts, isSecondaryPromptsLoading: false });
			},
		setData:
			(data: SuggestedPromptTile[]) =>
			({ setState }) => {
				setState({ data, isLoading: false });
			},
		shuffleSuggestions:
			() =>
			({ getState, setState }) => {
				const {
					primaryPrompts,
					secondaryPrompts,
					isPrimaryPromptsLoading,
					isSecondaryPromptsLoading,
				} = getState();

				if (
					isPrimaryPromptsLoading ||
					isSecondaryPromptsLoading ||
					!primaryPrompts ||
					!secondaryPrompts
				) {
					return;
				}

				const shuffledPrompts = shufflePromptBuckets({ primaryPrompts, secondaryPrompts });
				setState(shuffledPrompts);
			},
		setError:
			(error: Error) =>
			({ setState }) => {
				setState({
					error,
					isLoading: false,
					isPrimaryPromptsLoading: false,
					isSecondaryPromptsLoading: false,
				});
			},
	},
});

const useStore = createHook(store);
const useStoreActions = createActionsHook(store);

export const usePromptSuggestions: (
	props: AssistanceServiceInputParam,
) => UsePromptSuggestionsReturn = createHookWithAssistanceService(
	({
		assistanceServiceInjectedParams: { assistanceService },
	}: AssistanceServiceInjectedParams) => {
		const [state] = useStore();
		const {
			getState,
			setLoading,
			setPrimaryPrompts,
			setSecondaryPrompts,
			setData,
			shuffleSuggestions,
			setError,
		} = useStoreActions();

		const loadSuggestions = useCallback(async () => {
			const { isLoading, data, error, primaryPrompts, secondaryPrompts } = getState();
			if (isLoading || data || error || primaryPrompts || secondaryPrompts) {
				return;
			}

			setLoading();

			try {
				const { generator } = await assistanceService.fetchPersonalizedPromptSuggestionsStream({
					offset: PROMPT_SUGGESTIONS_OFFSET,
					limit: TOTAL_PROMPT_SUGGESTIONS_COUNT,
				});

				const prompts = await parsePromptSuggestionsStream({
					generator,
					onPrimaryPromptsReady: setPrimaryPrompts,
					onSecondaryPromptsReady: setSecondaryPrompts,
				});

				if (prompts.length === 0) {
					throw new Error('No prompt suggestions returned');
				}
				setData(prompts);
			} catch (err: unknown) {
				setError(err instanceof Error ? err : new Error('Failed to fetch prompt suggestions'));
			}
		}, [
			assistanceService,
			getState,
			setLoading,
			setPrimaryPrompts,
			setSecondaryPrompts,
			setData,
			setError,
		]);

		return {
			...state,
			loadSuggestions,
			shuffleSuggestions,
		};
	},
);
